<script lang="ts">
  import { goto, route } from '@mateothegreat/svelte5-router'
  import { logoDark, logoLight } from '../../assets/images'
  import i18n from '../../i18n'
  import Input from '../input/Input.svelte'
  import type { EventHandler } from 'svelte/elements'
  import { navbarSearchQuery } from '../../stores/navbar-search'
  import useAuth, { useCurrentUser } from '../../queries/auth'
  import { capitalize } from 'es-toolkit'
  import UserAvatar from '../user-avatar/UserAvatar.svelte'
  import {
    DropdownContainer,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
  } from '../dropdown'
  import { ChevronDown, LifeBuoy, Search } from '@lucide/svelte'
  import Button from '../button/Button.svelte'
  import Tooltip from '../tooltip/Tooltip.svelte'

  let searchQuery = $state($navbarSearchQuery)

  const { signoutMutation } = useAuth()
  const userQuery = useCurrentUser()
  const user = $derived(userQuery.data?.data)

  const onsubmit: EventHandler<SubmitEvent, HTMLFormElement> = (e) => {
    e.preventDefault()
    goto('/projects', { q: searchQuery })
  }
</script>

<div class="main-container">
  <div class="content">
    <a class="logo-container" href="/" use:route>
      <img
        class="logo dark-hidden"
        alt={$i18n.t('authLayout.logoAlt')}
        src={logoLight}
      />
      <img
        class="logo light-hidden"
        alt={$i18n.t('authLayout.logoAlt')}
        src={logoDark}
      />
    </a>

    {#if user}
      <form class="input-container" {onsubmit}>
        <Input
          name="search"
          placeholder={$i18n.t('navbar.searchPlaceholder')}
          rightIcon={Search}
          bind:value={searchQuery}
        />
      </form>

      <div class="right-container">
        <Tooltip title={$i18n.t('navbar.documentation.tooltip')}>
          <a
            class="navbar-button"
            href="https://docs.studio.constructions-3d.com"
            target="_blank"
          >
            <LifeBuoy />
          </a>
        </Tooltip>
        <DropdownContainer>
          <DropdownTrigger class="user-infos">
            {capitalize(user.username)}
            <div class="user-infos-avatar">
              <UserAvatar name={user.username} size={32} />
              <ChevronDown
                color="var(--icon-button-foreground)"
                size=".75rem"
              />
            </div>
          </DropdownTrigger>
          <DropdownContent placement="bottom-end">
            <DropdownItem
              onclick={() => {
                window.location.href = import.meta.env.VITE_OAUTH_URL
              }}>{$i18n.t('navbar.dropdown.settings')}</DropdownItem
            >
            <DropdownItem
              onclick={() => signoutMutation.mutate()}
              variant="destructive"
            >{$i18n.t('navbar.dropdown.signout')}</DropdownItem
            >
          </DropdownContent>
        </DropdownContainer>
      </div>
    {:else}
      <div class="buttons-container">
        <Button href="/signup" size="sm" variant="ghost">
          {$i18n.t('navbar.signUp')}
        </Button>
        <Button href="/signin" size="sm">
          {$i18n.t('navbar.signIn')}
        </Button>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  .main-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    border-bottom: 1px solid var(--grid-stroke);

    .logo-container {
      display: flex;
      align-items: center;
      justify-content: center;
      .logo {
        height: 2rem;
      }
    }

    .content {
      display: flex;
      flex-direction: row;
      gap: 2rem;
      justify-content: space-between;
      margin: 0 auto;
      max-width: 1216px;
      width: 100%;

      .input-container {
        max-width: 25rem;
        padding: 0.5rem 0;
        width: 100%;
      }

      .right-container {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        justify-content: flex-end;

        :global(.tooltip-container) {
          display: flex;
        }

        :global(.tooltip-content) {
          display: flex;
        }

        .navbar-button {
          align-items: center;
          background: none;
          border: 0;
          border-left: 1px solid var(--grid-stroke);
          color: inherit;
          cursor: pointer;
          display: flex;
          justify-content: center;
          padding: 0 1rem;
          transition: all 0.3s ease;

          &:hover {
            background-color: var(--input-background);
          }
        }

        :global(.user-infos) {
          align-items: center;
          display: flex;
          flex-direction: row;
          gap: 0.75rem;
          border-left: 1px solid var(--grid-stroke);
          padding-left: 1rem;
        }

        :global(.user-infos-avatar) {
          align-items: center;
          display: flex;
          flex-direction: row;
          gap: 0.25rem;
        }
      }

      .buttons-container {
        align-items: center;
        display: flex;
        flex-direction: row;
        gap: 0.75rem;
      }
    }
  }
</style>
